/**
 * Locale-Aware Formatting Utilities
 * 
 * Provides Intl-based formatting for dates, numbers, and currencies
 * that automatically adapt to the user's selected locale.
 */

import { useCurrentLocale } from "@/contexts/LocaleContext";
import { useMemo } from "react";

const LOCALE_MAP: Record<string, string> = {
  en: 'en-GB',          // International English → DD/MM/YYYY, £/€
  'en-US': 'en-US',     // US English → MM/DD/YYYY, $
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  pt: 'pt-PT',
};

function toIntlLocale(locale: string): string {
  return LOCALE_MAP[locale] ?? 'en-GB';
}

/** Default currency code for a given app locale */
export function getDefaultCurrency(locale: string): string {
  if (locale === 'en-US') return 'USD';
  return 'EUR';
}

export const formatters = {
  date: (date: Date, locale: string): string => {
    return new Intl.DateTimeFormat(toIntlLocale(locale), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  },

  dateDisplay: (date: Date, locale: string): string => {
    return new Intl.DateTimeFormat(toIntlLocale(locale), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  },

  dateTime: (date: Date, locale: string): string => {
    return new Intl.DateTimeFormat(toIntlLocale(locale), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  },

  currency: (amount: number, currency: string, locale: string): string => {
    return new Intl.NumberFormat(toIntlLocale(locale), {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  },

  number: (num: number, locale: string): string => {
    return new Intl.NumberFormat(toIntlLocale(locale)).format(num);
  },

  decimal: (num: number, locale: string, decimals: number = 1): string => {
    return new Intl.NumberFormat(toIntlLocale(locale), {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  },
};

/**
 * React hook providing locale-aware formatting functions.
 * Uses the current locale from LocaleContext.
 */
export function useLocaleFormatters() {
  const locale = useCurrentLocale();

  return useMemo(() => ({
    formatDate: (date: Date) => formatters.date(date, locale),
    formatDateDisplay: (date: Date) => formatters.dateDisplay(date, locale),
    formatDateTime: (date: Date) => formatters.dateTime(date, locale),
    formatCurrency: (amount: number, currency = 'EUR') => formatters.currency(amount, currency, locale),
    formatNumber: (num: number) => formatters.number(num, locale),
    formatDecimal: (num: number, decimals = 1) => formatters.decimal(num, locale, decimals),
  }), [locale]);
}
